import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import "../styles/LandingPage.css";

export default function LandingPage() {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is logged in, redirect to dashboard
        navigate("/doctor", { replace: true });
      } else {
        // User is not logged in, show landing page
        setCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Show loading while checking auth state
  if (checkingAuth) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            className="loading-spinner"
            style={{ margin: "0 auto 16px" }}
          ></div>
          <p style={{ color: "#64748b" }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      {/* Header/Navigation */}
      <header className="landing-header">
        <div className="header-content">
          <div className="logo-section">
            <h1 className="app-logo">
              <img src="/logo.png" alt="PhysioKea" className="logo-image" />
            </h1>
            <p className="tagline">
              Your Complete Physiotherapy Management Solution
            </p>
          </div>
          <nav className="header-nav">
            <button
              onClick={() => navigate("/login")}
              className="btn btn-secondary"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate("/register")}
              className="btn btn-primary"
            >
              Get Started
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="material-symbols-outlined">verified</span>
            <span>Trusted by Physiotherapists Worldwide</span>
          </div>
          <h1>
            Transform Your Practice with
            <span className="hero-highlight"> Smart Management</span>
          </h1>
          <p className="hero-description">
            The all-in-one platform designed for physiotherapists. Manage
            patients, schedule appointments, track progress, and grow your
            practice—all from one intuitive dashboard.
          </p>
          <div className="hero-buttons">
            <button
              onClick={() => navigate("/register")}
              className="btn btn-large btn-primary"
            >
              <span className="material-symbols-outlined">rocket_launch</span>
              Start Free Trial
            </button>
            <button
              onClick={() => navigate("/login")}
              className="btn btn-large btn-outline"
            >
              <span className="material-symbols-outlined">login</span>
              Sign In
            </button>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="hero-stat-icon">
                <span className="material-symbols-outlined">check_circle</span>
              </div>
              <div className="hero-stat-content">
                <span className="hero-stat-number">100%</span>
                <span className="hero-stat-label">Free to Start</span>
              </div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-icon">
                <span className="material-symbols-outlined">bolt</span>
              </div>
              <div className="hero-stat-content">
                <span className="hero-stat-number">5 min</span>
                <span className="hero-stat-label">Quick Setup</span>
              </div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-icon">
                <span className="material-symbols-outlined">cloud</span>
              </div>
              <div className="hero-stat-content">
                <span className="hero-stat-number">24/7</span>
                <span className="hero-stat-label">Cloud Access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <h2>Powerful Features</h2>
          <p>Everything you need to manage your practice efficiently</p>
        </div>
        <div className="features-grid">
          {/* Feature 1 */}
          <div className="feature-card">
            <div className="feature-icon-wrapper">
              <span className="material-symbols-outlined feature-icon">
                groups
              </span>
            </div>
            <h3>Patient Management</h3>
            <p>
              Comprehensive patient database with detailed records, medical
              history, and treatment notes—all organized in one secure place.
            </p>
            <ul>
              <li>Complete patient profiles</li>
              <li>Medical history tracking</li>
              <li>Treatment notes & progress</li>
              <li>Quick search & filtering</li>
            </ul>
          </div>

          {/* Feature 2 */}
          <div className="feature-card">
            <div className="feature-icon-wrapper">
              <span className="material-symbols-outlined feature-icon">
                calendar_month
              </span>
            </div>
            <h3>Smart Scheduling</h3>
            <p>
              Intuitive appointment management system that reduces no-shows and
              improves patient satisfaction with automated reminders.
            </p>
            <ul>
              <li>Easy appointment creation</li>
              <li>Calendar view & organization</li>
              <li>Multi-session scheduling</li>
              <li>Time-based grouping</li>
            </ul>
          </div>

          {/* Feature 3 */}
          <div className="feature-card">
            <div className="feature-icon-wrapper">
              <span className="material-symbols-outlined feature-icon">
                dashboard
              </span>
            </div>
            <h3>Practice Dashboard</h3>
            <p>
              Real-time overview of your practice with key metrics, patient
              statistics, and actionable insights at a glance.
            </p>
            <ul>
              <li>Real-time statistics</li>
              <li>Patient & appointment metrics</li>
              <li>Quick action shortcuts</li>
              <li>Visual analytics</li>
            </ul>
          </div>

          {/* Feature 4 */}
          <div className="feature-card">
            <div className="feature-icon-wrapper">
              <span className="material-symbols-outlined feature-icon">
                person
              </span>
            </div>
            <h3>Profile & Settings</h3>
            <p>
              Customize your experience with flexible settings. Configure your
              profile, clinic details, and preferences to match your needs.
            </p>
            <ul>
              <li>Complete profile management</li>
              <li>Clinic information setup</li>
              <li>Specialization tracking</li>
              <li>Privacy controls</li>
            </ul>
          </div>

          {/* Feature 5 */}
          <div className="feature-card">
            <div className="feature-icon-wrapper">
              <span className="material-symbols-outlined feature-icon">
                lock
              </span>
            </div>
            <h3>Secure & Reliable</h3>
            <p>
              Enterprise-grade security protecting your data with encryption,
              secure authentication, and HIPAA-compliant infrastructure.
            </p>
            <ul>
              <li>Secure authentication</li>
              <li>End-to-end encryption</li>
              <li>Automated backups</li>
              <li>HIPAA compliance</li>
            </ul>
          </div>

          {/* Feature 6 */}
          <div className="feature-card">
            <div className="feature-icon-wrapper">
              <span className="material-symbols-outlined feature-icon">
                phone_iphone
              </span>
            </div>
            <h3>Mobile-First Design</h3>
            <p>
              Beautiful, intuitive interface designed for modern devices. Access
              your practice from anywhere, anytime.
            </p>
            <ul>
              <li>Modern, clean design</li>
              <li>Intuitive navigation</li>
              <li>Quick onboarding</li>
              <li>Fully responsive</li>
            </ul>
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="how-to-use-section">
        <div className="section-header">
          <h2>Get Started in 5 Simple Steps</h2>
          <p>Start managing your practice in minutes, not hours</p>
        </div>
        <div className="steps-container">
          <div className="step">
            <div className="step-icon-wrapper">
              <div className="step-number">1</div>
              <span className="material-symbols-outlined step-icon">
                person_add
              </span>
            </div>
            <h3>Create Account</h3>
            <p>
              Sign up with your email. Free to start, no credit card required.
            </p>
          </div>

          <div className="step-connector">
            <span className="material-symbols-outlined">arrow_forward</span>
          </div>

          <div className="step">
            <div className="step-icon-wrapper">
              <div className="step-number">2</div>
              <span className="material-symbols-outlined step-icon">edit</span>
            </div>
            <h3>Set Up Profile</h3>
            <p>
              Complete your profile with clinic details and specializations.
            </p>
          </div>

          <div className="step-connector">
            <span className="material-symbols-outlined">arrow_forward</span>
          </div>

          <div className="step">
            <div className="step-icon-wrapper">
              <div className="step-number">3</div>
              <span className="material-symbols-outlined step-icon">
                groups
              </span>
            </div>
            <h3>Add Patients</h3>
            <p>
              Import or manually add your patients with their medical
              information.
            </p>
          </div>

          <div className="step-connector">
            <span className="material-symbols-outlined">arrow_forward</span>
          </div>

          <div className="step">
            <div className="step-icon-wrapper">
              <div className="step-number">4</div>
              <span className="material-symbols-outlined step-icon">event</span>
            </div>
            <h3>Schedule Sessions</h3>
            <p>
              Create appointments and manage your clinic's schedule efficiently.
            </p>
          </div>

          <div className="step-connector">
            <span className="material-symbols-outlined">arrow_forward</span>
          </div>

          <div className="step">
            <div className="step-icon-wrapper">
              <div className="step-number">5</div>
              <span className="material-symbols-outlined step-icon">
                dashboard
              </span>
            </div>
            <h3>Start Managing</h3>
            <p>Monitor your practice, track progress, and grow your clinic.</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="section-header">
          <h2>Why Choose PhysioKea?</h2>
          <p>Everything you need to run a successful practice</p>
        </div>
        <div className="benefits-grid">
          <div className="benefit-item">
            <div className="benefit-icon-wrapper">
              <span className="material-symbols-outlined benefit-icon">
                schedule
              </span>
            </div>
            <h4>Save Time</h4>
            <p>
              Automate administrative tasks and focus on what you do
              best—patient care
            </p>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon-wrapper">
              <span className="material-symbols-outlined benefit-icon">
                folder_managed
              </span>
            </div>
            <h4>Stay Organized</h4>
            <p>
              All patient and appointment data organized and accessible in one
              place
            </p>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon-wrapper">
              <span className="material-symbols-outlined benefit-icon">
                favorite
              </span>
            </div>
            <h4>Better Care</h4>
            <p>
              Provide professional, efficient service that keeps patients coming
              back
            </p>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon-wrapper">
              <span className="material-symbols-outlined benefit-icon">
                security
              </span>
            </div>
            <h4>Secure & Private</h4>
            <p>
              Enterprise-grade security protecting sensitive patient information
            </p>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon-wrapper">
              <span className="material-symbols-outlined benefit-icon">
                bolt
              </span>
            </div>
            <h4>Quick Setup</h4>
            <p>
              Get started in minutes with our intuitive, user-friendly interface
            </p>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon-wrapper">
              <span className="material-symbols-outlined benefit-icon">
                cloud
              </span>
            </div>
            <h4>Always Available</h4>
            <p>Access your practice data from anywhere, on any device, 24/7</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <div className="cta-badge">
            <span className="material-symbols-outlined">stars</span>
            <span>Join Thousands of Physiotherapists</span>
          </div>
          <h2>Ready to Transform Your Practice?</h2>
          <p className="cta-description">
            Start managing your practice more efficiently today. Join
            physiotherapists worldwide who trust PhysioKea to streamline
            operations and deliver exceptional patient care.
          </p>
          <div className="cta-features">
            <div className="cta-feature">
              <span className="material-symbols-outlined">check_circle</span>
              <span>No credit card required</span>
            </div>
            <div className="cta-feature">
              <span className="material-symbols-outlined">check_circle</span>
              <span>Setup in 5 minutes</span>
            </div>
            <div className="cta-feature">
              <span className="material-symbols-outlined">check_circle</span>
              <span>24/7 support</span>
            </div>
            <div className="cta-feature">
              <span className="material-symbols-outlined">check_circle</span>
              <span>Cloud-based access</span>
            </div>
          </div>
          <div className="cta-buttons">
            <button
              onClick={() => navigate("/register")}
              className="btn btn-large btn-primary cta-primary-btn"
            >
              <span className="material-symbols-outlined">rocket_launch</span>
              Get Started Free
            </button>
            <button
              onClick={() => navigate("/login")}
              className="btn btn-large btn-secondary cta-secondary-btn"
            >
              <span className="material-symbols-outlined">login</span>
              Sign In
            </button>
          </div>
          <p className="cta-trust">
            <span className="material-symbols-outlined">verified</span>
            Trusted by physiotherapy clinics worldwide
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>PhysioKea</h4>
            <p>Your trusted physiotherapy management solution</p>
          </div>
          <div className="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li>
                <button onClick={() => navigate("/login")} className="link-btn">
                  Sign In
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("/register")}
                  className="link-btn"
                >
                  Register
                </button>
              </li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Support</h4>
            <ul>
              <li>
                <button
                  onClick={() => navigate("/help")}
                  className="link-btn"
                >
                  Help & Documentation
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("/contact")}
                  className="link-btn"
                >
                  Contact Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("/privacy-security")}
                  className="link-btn"
                >
                  Privacy Policy
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 PhysioKea. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
